import { ReleaseNoteProduct } from '@features/components/ReleaseNotes';
import { useDeleteReleaseNoteMutation, useReleaseNotesQuery } from '@/queries/release-notes';
import { useDeleteOpobffReleaseNoteMutation, useOpobffReleaseNotesQuery } from '@/queries/opobff';
import { useDeleteResourceReleaseNoteMutation, useResourceReleaseNotesQuery } from '@/queries/resource/release-notes';

/**
 * Backing API for a release-note product:
 * - TXC Access  → customerservice
 * - OPO Access  → opobff
 * - Nexus, Live → resourceservice (single endpoint, `product`-scoped)
 */
export enum ReleaseNoteApiSource {
  Customer = 'customer',
  Opo = 'opo',
  Resource = 'resource',
}

const PRODUCT_API_SOURCE: Record<ReleaseNoteProduct, ReleaseNoteApiSource> = {
  [ReleaseNoteProduct.TxcAccess]: ReleaseNoteApiSource.Customer,
  [ReleaseNoteProduct.OpoAccess]: ReleaseNoteApiSource.Opo,
  [ReleaseNoteProduct.Nexus]: ReleaseNoteApiSource.Resource,
  [ReleaseNoteProduct.Live]: ReleaseNoteApiSource.Resource,
};

export const getReleaseNoteApiSource = (product: ReleaseNoteProduct): ReleaseNoteApiSource =>
  PRODUCT_API_SOURCE[product];

/**
 * Selects the delete mutation matching the active product's backing API.
 */
export const useReleaseNoteDeleteMutation = (product: ReleaseNoteProduct) => {
  const txcDeleteMutation = useDeleteReleaseNoteMutation();
  const opoDeleteMutation = useDeleteOpobffReleaseNoteMutation();
  const resourceDeleteMutation = useDeleteResourceReleaseNoteMutation(product);

  const source = getReleaseNoteApiSource(product);
  if (source === ReleaseNoteApiSource.Opo) return opoDeleteMutation;
  if (source === ReleaseNoteApiSource.Resource) return resourceDeleteMutation;
  return txcDeleteMutation;
};

export const useReleaseNotesByProductQuery = (product: ReleaseNoteProduct) => {
  const source = getReleaseNoteApiSource(product);

  const txcQuery = useReleaseNotesQuery(undefined, { enabled: source === ReleaseNoteApiSource.Customer });
  const opoQuery = useOpobffReleaseNotesQuery(undefined, { enabled: source === ReleaseNoteApiSource.Opo });
  const resourceQuery = useResourceReleaseNotesQuery(product, undefined, {
    enabled: source === ReleaseNoteApiSource.Resource,
  });

  if (source === ReleaseNoteApiSource.Opo) return opoQuery;
  if (source === ReleaseNoteApiSource.Resource) return resourceQuery;
  return txcQuery;
};
